import * as React from 'react';
import { useMemo, useState, useEffect, useCallback } from 'react';
import * as R from 'ramda';
import ProjectLayout from '../ProjectLayout';
import { useDispatch } from 'react-redux';
import { useMount } from '../../core/hooks/useMount';
import { openMenuAction } from '../sideMenu/openMenuAction';
import { onMobileDevice } from '../../globalStyle/mediaQueries';
import { useWindowSize } from '../../core/hooks/useWindowSize';
import { css } from '@emotion/core';
import { useMsgText } from '../../localization/hooks';
import { useAppState } from '../../core/redux/hooks';
import { Str } from '../../localization/translationKeys';
import SurveyHeader from '../survey/SurveyHeader';
import LocalDiv from '../../localization/LocalDiv';
import ReportTable from './ReportTable';
import { useLoading, useError } from '../../core/api/hooks';
import { ActionType } from '../../core/redux/actionType';
import { loadReportsAction } from './api/loadReportsAction';
import { Report } from './models';
import { Loader } from '../../loading/Loader';
import { useToggle } from '../../core/hooks/useToggle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWrench } from '@fortawesome/free-solid-svg-icons';
import { COLORS } from '../../globalStyle/colors';
import Spacer from '../../layout/Spacer';
import Msg from '../../localization/Msg';
import { FLEX_COLUMN, FLEX_ROW } from '../../layout/flexBox';
import CompetencyList from './CompetencyList';
import { getDevPlanItemsAction } from '../devPlan/api/getDevPlanItemsAction';
import { addDevPlanItemAction } from '../devPlan/api/addDevPlanItemAction';
import { deleteDevPlanItemAction } from '../devPlan/api/deleteDevPlanItemAction';
import { DevPlanItem } from '../devPlan/models';
import { SELECT } from '../../forms/styles/select';
import { useProjIds } from '../../navigation/hooks';
import ToggleSwitch from '../../layout/ToggleSwitch';
import { pathCombine } from '../../core/api/module';
import { BaseUrl } from '../../core/api/endpoints';
import ConfirmationModal from '../../layout/modal/ConfirmationModal';
import { isRtl } from '../../localization/module';
import { useBrowser } from '../../language/useBrowser';
import { eqScoresEmailPreviewAction } from './api/eqScoresEmailPreviewAction';
import { useSendEQScoresModalProps } from './hooks';
import SendEQScoresModal from './SendEQScoresModal';
import { IS_DEV_ENV } from '../../core/windowProps';


const styles = {
  content:       css`
    margin-top: 57px;

    ${onMobileDevice} {
      margin-top: 20px;
    }
  `,
  header:        css`
    ${onMobileDevice} {
      margin-top: 37px;
    }
  `,
  reportContent: css`
    margin-top: 0;
  `,
  iframe:        css`
    width: 100%;
    height: calc(100vh - 53px);
  `,
  loader:        css`
    width: 150px;
    height: 150px;
    margin: auto;
  `,
  msgRow:        css`
    flex-grow: 1;
    align-items: stretch;
    justify-content: flex-end;
    margin: 10px 10px 5px;
  `,
  icon:          css`
    color: ${COLORS.DARK_GREY};
    margin-top: 8px;
  `,
  msg:           css`
    margin-top: 8px;
  `,
  select:        css`
    padding: 9px 15px;
    background-color: ${COLORS.WHITE};
    height: auto;
  `,
  notFoundRow:   css`
    margin: 70px 50px;
  `,
  notFound:      css`
    font-size: 16px;
  `,
};


type Props = {
  className?: string;
};


/**
 * FeedbackReport: a component that renders the report of feedback a survey has received
 *
 * PureComponent: true
 * Redux Connected: false
 */
const FeedbackReport: React.FC<Props> = props => {
  const dispatch = useDispatch();

  const projIds       = useProjIds();
  const participantId = projIds.participantId;
  const projectId     = projIds.projId;

  const devPlanProjectId = useAppState(state => state.projectSummary?.devPlanProjectId || projectId);

  useMount(() => { dispatch(getDevPlanItemsAction(devPlanProjectId, participantId)); });

  const locale = useAppState(state => state.locale);
  const langId = useMemo(() => locale.lang.languageId, [locale]);
  const rtl    = useMemo(() => isRtl(locale.textDirection), [locale]);
  const code   = useAppState(state => state.locale.lang.code);

  useEffect(
    () => {
      if (!!langId) {
        dispatch(eqScoresEmailPreviewAction(langId));
      }
    },
    [dispatch, langId],
  );

  const pageTitle = useMsgText(Str.ViewReport);
  const project   = useAppState(state => state.projectSummary);
  const reports   = useAppState(state => state.reports);
  const userName  = useAppState(state => state.auth.currentUser?.userName);
  const compCats  = useAppState(state => state.devResources || []);
  const devItems  = useAppState(state => state.devPlan?.items ?? []);

  const [loadingReport, toggleLoadingReport] = useToggle();
  const [iFrameFirstLoad, setIframeLoaded] = useToggle();

  const [oneReport, setReport] = useState<Report | undefined>(undefined);

  const isLoading = useLoading(ActionType.LoadReportsRequest);
  const error     = useError(ActionType.LoadReportsRequest);

  const reportsNotQueried = useMemo(() => R.isNil(reports), [reports]);
  const oldCount = useMemo(() => !reports ? 0 : reports.length, [reports]);
  const newCount = useMemo(() => project?.reportCount, [project]);

  useMount(() => {
    toggleLoadingReport();

    if (reportsNotQueried || (oldCount !== newCount)) {
      dispatch(loadReportsAction(projectId, participantId, userName!));
    }
  });

  //region UI switches

  const { onIe, onSafari } = useBrowser();
  const { isMobile }       = useWindowSize();
  const system             = useAppState(state => state.config?.system.toLocaleLowerCase() ?? '');
  const portalIsTSEQ       = useMemo(() => system === 'talentsmart', [system]);

  // Fall back to download function if no web report and using legacy browser
  const enableReportDownload = useCallback((report: Report) => 
    (onIe || (onSafari && isMobile)) && !report.webReport, [onIe, onSafari, isMobile],
  );

  // Determines whether to show the share button on individual report rows in the table
  const isMEReport  = useCallback((report: Report) => report.surveyId === 1, []);
  const hasMEReport = useMemo(() => !!reports?.find(r => isMEReport(r)), [isMEReport, reports]);

  // Determines whether to show the share button column in the table
  // Currently this is a TSEQ-only feature and is only applicable to ME reports
  const enableSharingScores = useMemo(() => hasMEReport && portalIsTSEQ, [hasMEReport, portalIsTSEQ]);

  // Determines whether to show the status column in the table
  const showStatus = useMemo(() => !!project?.extraReportGeneration, [project]);

  const hasOneReport = useMemo(() => reports?.length === 1, [reports]);

  const autoLoadReport = useMemo(
    () => {
      if (!hasOneReport) {
        return false;
      }

      const report = R.path<Report>([0], reports)!;

      if (enableReportDownload(report)) {
        return false;
      }

      return !enableSharingScores;
    },
    [enableReportDownload, enableSharingScores, hasOneReport, reports],
  );

  useEffect(
    () => {
      if (autoLoadReport) {
        setReport(reports![0]);
      }
    },
    [setReport, reports, onIe, onSafari, autoLoadReport],
  );

  // endregion UI switches

  const [idToDelete, setIdToDelete] = useState<number>(0);
  const competenciesText            = useMsgText(Str.ChooseCompetencies);

  const showCompetencies = oneReport
                           && oneReport.webReport
                           && project?.developmentPlanAddCompetenciesFromWebReport
                           && project?.developmentPlanStep;

  const [showList, toggleList] = useToggle();

  const [showDeleteConfirmation, toggleShowDeleteConfirmation] = useToggle(false);

  const { addItem, deleteItem } = useMemo(
    () => ({
      addItem:    (competencyId: number) => dispatch(
        addDevPlanItemAction(participantId, devPlanProjectId, competencyId, undefined),
      ),
      deleteItem: (competencyId: number) => {
        const devPlanItem = R.find<DevPlanItem>(item => item.devItemCompetencyId === competencyId, devItems);
        const devItemId   = devPlanItem?.devItemId ?? 0;

        if (devPlanItem?.hasContent && competencyId > 0) {
          setIdToDelete(competencyId);
          toggleShowDeleteConfirmation();
        } else {
          dispatch(deleteDevPlanItemAction(participantId, devPlanProjectId, devItemId));
        }
      },
    }),
    [dispatch, participantId, devPlanProjectId, devItems, toggleShowDeleteConfirmation],
  );

  const checkMenu = () => {
    // Close the side menu when showing a web report to maximize space
    if (oneReport && oneReport.webReport) {
      window.setTimeout(() => dispatch(openMenuAction(false)), 200);
    }

    return oneReport && oneReport.webReport;
  };

  const fileURL = useMemo(
    () => {
      const baseUrl = pathCombine(BaseUrl, '/report/reportFile');

      return oneReport?.fileExists ? baseUrl + '/' + encodeURIComponent(oneReport!.reportFile) : undefined;
    },
    [oneReport],
  );

  const competencyName        = useMemo(
    () => {
      const competencyId = idToDelete;
      const devPlanItem  = R.find<DevPlanItem>(item => item.devItemCompetencyId === competencyId, devItems);

      return devPlanItem?.title ?? '';
    },
    [idToDelete, devItems],
  );
  const deleteConfirmationMsg = useMsgText(Str.DevPlanContentExists, '', { 'name': competencyName });

  const onConfirmDelete = useCallback(
    () => {
      const competencyId = idToDelete;
      const devPlanItem  = R.find<DevPlanItem>(item => item.devItemCompetencyId === competencyId, devItems);
      const devItemId    = devPlanItem?.devItemId ?? 0;

      dispatch(deleteDevPlanItemAction(participantId, devPlanProjectId, devItemId));
      toggleShowDeleteConfirmation();
    },
    [idToDelete, devItems, devPlanProjectId, participantId, toggleShowDeleteConfirmation, dispatch],
  );

  const showHeader = useMemo(
    () => (!oneReport || (!oneReport.webReport && !oneReport.fileExists)),
    [oneReport],
  );

  const reportWebUrl = useCallback(
    (report: Report) => IS_DEV_ENV
      ? report.reportUrl.replace(
        'http://localhost:81/logi',
        'https://report.truscore.com/LogiTSEQstage',
      )
      : report.reportUrl,
    [],
  );

  const courseCompletionMsg = useMsgText(
    Str.CourseCompletionMsg).replace('{percent}', 
    project?.courseCompletionPct?.toString() || '',
  );

  const {
          sendEQScoresModalVisible,
          sendEQScoresEmailPreviewRoute,
          showSendEQScoresModal,
          dismissSendEQScoresModal,
          setEQScoresRecipientComment,
          setEQScoresRecipientEmail,
          onConfirmShareEQScores,
        } = useSendEQScoresModalProps(projectId, participantId, dispatch);

  return (
    <ProjectLayout
      floatMenu={checkMenu()}
      className={props.className}
      contentStyle={
        showHeader
            ? css`max-width: 100%;`
            : rtl
              ? css`padding: 0 50px 0 0;
                    max-width: 100%;`
              : css`padding: 0 0 0 50px;
                    max-width: 100%;`
      }
    >
      {
        showHeader && <SurveyHeader title={`/ ${pageTitle}`} deadline={undefined} css={styles.header} />
      }
      <LocalDiv css={oneReport ? styles.reportContent : styles.content}>
        {
          oneReport
            ? (
              <>
                {
                  loadingReport && <Loader css={styles.loader} />
                }
                {
                  showCompetencies && !loadingReport && (
                    <LocalDiv css={[FLEX_ROW, styles.msgRow]}>
                      <FontAwesomeIcon icon={faWrench} css={[FLEX_COLUMN, styles.icon]} size={'1x'} />
                      <Spacer sizePx={10} css={FLEX_COLUMN} />
                      <Msg str={Str.WebReportMsg} css={[FLEX_COLUMN, styles.msg]} />
                      <Spacer sizePx={10} css={FLEX_COLUMN} />
                      <LocalDiv css={[SELECT, FLEX_COLUMN, styles.select]} onClick={toggleList}>
                        {competenciesText}
                      </LocalDiv>
                      {
                        showList && (
                          <CompetencyList
                            competencies={compCats}
                            devItems={devItems}
                            addItem={addItem}
                            deleteItem={deleteItem}
                          />
                        )
                      }
                    </LocalDiv>
                  )
                }

                {
                  oneReport.webReport || oneReport.fileExists
                    ? <iframe
                        frameBorder={0}
                        css={styles.iframe}
                        src={oneReport.webReport ? reportWebUrl(oneReport) : fileURL}
                        title='view-report'
                        onLoad={
                          () => {
                            if (!iFrameFirstLoad) {
                              setIframeLoaded();
                              toggleLoadingReport();
                            }
                          }
                        }
                      />
                    : <LocalDiv css={styles.notFoundRow}>
                        <ToggleSwitch toggleFn={toggleLoadingReport} />
                        <Msg str={Str.NotAvailable} css={styles.notFound} />
                      </LocalDiv>
                }

                { // Delete Confirmation Modal
                  showDeleteConfirmation && (
                    <ConfirmationModal
                      confirmationTxt={deleteConfirmationMsg}
                      primaryActionStr={Str.No}
                      secondaryActionStr={Str.Yes}
                      onSelectPrimary={toggleShowDeleteConfirmation}
                      onSelectSecondary={onConfirmDelete}
                      onDismiss={toggleShowDeleteConfirmation}
                    />
                  )
                }
              </>
            )
            : (
              <ReportTable
                project={project}
                reports={reports}
                showShareScoresColumn={enableSharingScores}
                showStatusColumn={showStatus}
                courseCompletionMsg={courseCompletionMsg}
                loading={isLoading}
                error={!!error}
                showShareScoresBtn={isMEReport}
                enableReportDownload={enableReportDownload}
                setReport={setReport}
                onClickShareScoresBtn={showSendEQScoresModal}
                code={code}
              />
            )
        }
      </LocalDiv>
      {
        sendEQScoresModalVisible && (
          <SendEQScoresModal
            dismissModal={dismissSendEQScoresModal}
            setEQScoresRecipientEmail={setEQScoresRecipientEmail}
            setEQScoresRecipientComment={setEQScoresRecipientComment}
            onConfirmShareEQScores={onConfirmShareEQScores}
            templatePreviewRoute={sendEQScoresEmailPreviewRoute}
          />
        )
      }
    </ProjectLayout>
  );
};

export default FeedbackReport;
